import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { JobCardStatusBadge } from '@/components/shared/JobCardStatusBadge';
import { InvoiceStatusBadge } from '@/components/shared/InvoiceStatusBadge';
import type { Job } from '@jobos/shared/types/job';
import type { Location } from '@jobos/shared/types/location';

// Extended Job type with location for client detail view
type JobWithLocation = Job & { location?: Location };

interface JobsSectionProps {
  clientId: string;
  jobs: JobWithLocation[];
}

export function JobsSection({ clientId, jobs }: JobsSectionProps) {
  return (
    <div className="bg-white shadow rounded-lg">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Jobs ({jobs.length})
          </h2>
          <Link to={`/jobs/new?clientId=${clientId}`}>
            <Button size="sm">Create Job</Button>
          </Link>
        </div>

        {jobs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead>Project Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="max-w-xs truncate">
                    {job.location ? (
                      <>
                        {job.location.addressLine1 || job.location.name}
                        {job.location.addressLine2 && `, ${job.location.addressLine2}`}
                        {job.location.city && `, ${job.location.city}`}
                      </>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="font-medium">
                    {job.projectType === 'Other' && job.customProjectType
                      ? job.customProjectType
                      : job.projectType}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {job.description || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <JobStatusBadge status={job.status} />
                      <JobCardStatusBadge status={job.jobCardStatus} />
                      <InvoiceStatusBadge
                        status={job.invoiceStatus}
                        lastInvoicedDate={job.lastInvoicedDate}
                        billingType={job.billingType}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {job.startDate
                      ? new Date(job.startDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link to={`/jobs/${job.id}`}>
                      <Button variant="outline" size="sm">
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No jobs found for this client</p>
            <Link to={`/jobs/new?clientId=${clientId}`}>
              <Button className="mt-4" size="sm">
                Create First Job
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
